import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EstimateEdit = () => {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    address: '',
    current_roof_type: 'asphalt_shingle',
    notes: ''
  });

  useEffect(() => {
    loadEstimate();
  }, [estimateId]);

  const loadEstimate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/estimates/${estimateId}`);
      setEstimate(response.data);
      setFormData({
        customer_first_name: response.data.customer_first_name || '',
        customer_last_name: response.data.customer_last_name || '',
        customer_email: response.data.customer_email || '',
        customer_phone: response.data.customer_phone || '',
        address: response.data.address || '',
        current_roof_type: response.data.current_roof_type || 'asphalt_shingle',
        notes: response.data.notes || ''
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading estimate:', error);
      alert('Failed to load estimate');
      navigate('/dashboard/estimates');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await axios.put(`${BACKEND_URL}/api/estimates/${estimateId}`, formData);
      alert('Estimate updated successfully!');
      navigate(`/dashboard/estimates/${estimateId}`);
    } catch (error) {
      console.error('Error updating estimate:', error);
      alert('Failed to update estimate');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading estimate...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/dashboard/estimates/${estimateId}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Estimate
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Edit Estimate</h1>
        <p className="text-gray-600 mt-2">Estimate ID: {estimateId}</p>
      </div>

      {/* Edit Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Customer Information */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={formData.customer_first_name}
                onChange={(e) => handleChange('customer_first_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={formData.customer_last_name}
                onChange={(e) => handleChange('customer_last_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleChange('customer_email', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => handleChange('customer_phone', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Property Information */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Property Information</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="address">Property Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Address cannot be changed (satellite data linked)</p>
            </div>
            <div>
              <Label htmlFor="roof_type">Current Roof Type</Label>
              <select
                id="roof_type"
                value={formData.current_roof_type}
                onChange={(e) => handleChange('current_roof_type', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="landmark">Landmark (Baseline Architectural)</option>
                <option value="landmark_pro">Landmark PRO</option>
                <option value="standing_seam">Standing Seam Metal</option>
                <option value="luxury">Luxury Line</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes</h2>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            placeholder="Add any notes about this estimate..."
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => navigate(`/dashboard/estimates/${estimateId}`)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Changes to customer information and notes will be saved. 
          Satellite measurements and pricing are recalculated automatically and cannot be manually edited.
        </p>
      </div>
    </div>
  );
};

export default EstimateEdit;
